import { API_CONFIG } from '../config/api';
import { getCurrentUserId } from './auth';
import { ApiValidationError } from '../types/auth';
import type {
  Supplier,
  CreateSupplierData,
  UpdateSupplierData,
  SuppliersResponse,
  SuppliersListParams,
  SuppliersListResult,
  SupplierResponse,
  SupplierOpenBills,
  SupplierOpenBillsResponse,
  RecordBulkSupplierPaymentData,
  BulkSupplierPaymentResult,
  BulkSupplierPaymentResponse,
  SupplierPaymentMethod,
} from '../types/supplier';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getSuppliers = async (params?: SuppliersListParams): Promise<SuppliersListResult> => {
  const queryParams = new URLSearchParams();

  if (params?.search) {
    queryParams.set('search', params.search);
  }
  if (params?.payment_terms) {
    queryParams.set('payment_terms', params.payment_terms);
  }
  if (params?.active != null) {
    queryParams.set('active', params.active ? 'true' : 'false');
  }
  if (params?.has_balance != null) {
    queryParams.set('has_balance', params.has_balance ? 'true' : 'false');
  }
  if (params?.sort) {
    queryParams.set('sort', params.sort);
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch vendors');
  }

  const result: SuppliersResponse = await response.json();
  const meta = result.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: result.data?.length ?? 0,
    total: result.data?.length ?? 0,
  };

  return {
    suppliers: result.data ?? [],
    meta,
  };
};

/** Fetches every page (for dropdowns and pickers that need the full vendor list). */
export const getAllSuppliers = async (): Promise<Supplier[]> => {
  const all: Supplier[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    const result = await getSuppliers({ per_page: 100, page });
    all.push(...result.suppliers);
    lastPage = result.meta.last_page;
    page += 1;
  } while (page <= lastPage);

  return all;
};

export const getSupplierById = async (id: number): Promise<Supplier> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.GET_ALL}/${id}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch vendor');
  }

  const result: SupplierResponse = await response.json();
  return result.data;
};

export const createSupplier = async (supplierData: CreateSupplierData): Promise<Supplier> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.GET_ALL}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(supplierData),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create vendor');
  }

  const result: SupplierResponse = await response.json();
  return result.data;
};

export const updateSupplier = async (
  supplierId: number,
  supplierData: UpdateSupplierData
): Promise<Supplier> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.GET_ALL}/${supplierId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(supplierData),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update vendor');
  }

  const result: SupplierResponse = await response.json();
  return result.data;
};

export const deleteSupplier = async (id: number): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.GET_ALL}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete vendor');
  }
};

export const getSupplierOpenBills = async (supplierId: number): Promise<SupplierOpenBills> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.OPEN_BILLS}/${supplierId}/open-bills`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch open bills');
  }

  const result: SupplierOpenBillsResponse = await response.json();
  return result.data;
};

export const recordBulkSupplierPayment = async (
  supplierId: number,
  paymentMethod: SupplierPaymentMethod,
  allocations: { purchase_id: number; amount: number }[],
  options?: { notes?: string; paymentDate?: string; referenceNo?: string }
): Promise<BulkSupplierPaymentResult> => {
  const amount = allocations.reduce((sum, row) => sum + row.amount, 0);

  const payload: RecordBulkSupplierPaymentData = {
    amount,
    payment_method: paymentMethod,
    reference_no: options?.referenceNo?.trim() || `BPAY-${Date.now()}`,
    payment_date: options?.paymentDate,
    allocation_strategy: 'manual',
    allocations,
    notes: options?.notes,
    created_by: getCurrentUserId(),
  };

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUPPLIERS.BULK_PAYMENT}/${supplierId}/payments/bulk`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to record bulk payment');
  }

  const result: BulkSupplierPaymentResponse = await response.json();
  return result.data;
};
